import React from 'react';
import { observer } from 'mobx-react-lite';
import globalUserProfile from '../../../../stores/global-user-profile';
import { StudentProfile } from '../../../../api/types/settings/settings-all';
import { ExamStudentWithCourseDetails } from '../../../../api/types/students/exams/exam-student-response';
import { ExamStudentEligibility } from '../../../../api/types/programs/course/exams/exam-sessions-enrollment-entity';
import { FlattenedExamSession } from './types/exam-types';
import { OpenForRegistrationExams } from './components/open-for-registration-exams';
import { UpcomingExams } from './components/upcoming-exams';
import { HistoricalExams } from './components/historical-exams';

// Helper function to flatten exam data into session rows
const flattenExamSessions = (
  exams: ExamStudentWithCourseDetails[],
): FlattenedExamSession[] => {
  const flattened: FlattenedExamSession[] = [];

  exams.forEach((exam) => {
    exam.sessions.forEach((session) => {
      flattened.push({
        examId: exam.id,
        examName: exam.examName,
        courseName: exam.courseName,
        programId: exam.programId,
        programName: exam.programName,
        sessionId: session.id,
        sessionType: session.sessionType,
        sessionDate: session.sessionDate,
        registrationStartDate: session.registrationStartDate,
        registrationEndDate: session.registrationEndDate,
        eligibility: session.enrollment?.examEligibility,
        isEnrolled: session.enrollment?.enrollment === true,
        attended: session.enrollment?.attended,
        grade: session.enrollment?.grade,
        scheduling: session.scheduling,
      });
    });
  });

  return flattened;
};

export const MyExams = observer(() => {
  // Check if user profile exists and is a student
  if (!globalUserProfile.userProfile?.profile) {
    return <div>Loading user profile...</div>;
  }

  const studentProfile = globalUserProfile.userProfile
    .profile as StudentProfile;
  const exams: ExamStudentWithCourseDetails[] = studentProfile.exams || [];

  // Flatten all exam sessions
  const allSessions = flattenExamSessions(exams);
  const now = new Date();

  // 1. מבחנים פתוחים להרשמה - Open for registration
  const openForRegistration = allSessions.filter((session) => {
    const regStart = new Date(session.registrationStartDate);
    const regEnd = new Date(session.registrationEndDate);
    const isInRegistrationPeriod = now >= regStart && now <= regEnd;
    const isEligible =
      session.eligibility === ExamStudentEligibility.ELIGIBILITY ||
      session.eligibility === ExamStudentEligibility.EXCLUDED;
    const isNotEnrolled = !session.isEnrolled;

    return isInRegistrationPeriod && isEligible && isNotEnrolled;
  });

  // 2. המבחנים הקרובים - Upcoming exams (enrolled and not attended yet)
  const upcomingExams = allSessions.filter((session) => {
    return session.isEnrolled && session.attended == null;
  });

  // 3. מבחנים היסטוריים - Historical exams (attended is set)
  const historicalExams = allSessions.filter((session) => {
    return session.attended != null;
  });

  return (
    <div style={{ paddingTop: '16px' }}>
      <div
        style={{
          color: '#041E49',
          fontSize: '30px',
          fontWeight: 'bold',
          marginBottom: '24px',
        }}
      >
        המבחנים שלי
      </div>

      <OpenForRegistrationExams sessions={openForRegistration} />
      <UpcomingExams sessions={upcomingExams} />
      <HistoricalExams sessions={historicalExams} />
    </div>
  );
});
